import GridOnIcon from "@mui/icons-material/GridOn";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useActiveFBOs } from "../../../../../containers/ActiveFBOContainer";
import { MetricAwareTextField } from "../../../../../widgets/MetricAwareTextField/MetricAwareTextField";
import { useLocalRampState } from "../RampEditorPresenter";
import {
  SIZE_CATEGORY_OPTIONS,
  Shape,
  SpotMetadata,
  getDefaultSizeCategory,
  getMaxAllowedSizes,
} from "./RampCanvas";
import { SpotTilePicker } from "./SpotTilePicker";
import { calculateSpotNames, tileSpot } from "./spotHelpers";
import { detectRequiredSpots } from "./utils/gapSpotDetection";

type Props = {
  shape: Shape;
  shapes: Shape[];
  activeShapes: string[];
  contextMenu: { shapeId: string } | null;
  setShapes: (shapes: Shape[]) => void;
  canvasWidth: number;
  previewSpots: Shape[];
  setPreviewSpots: (spots: Shape[]) => void;
};

export const SpotPropertiesSection: React.FC<Props> = ({
  shape,
  shapes,
  activeShapes,
  contextMenu,
  setShapes,
  canvasWidth,
  previewSpots,
  setPreviewSpots,
}) => {
  const { ramp } = useLocalRampState();
  const { activeFBO } = useActiveFBOs();
  const [tileAnchorEl, setTileAnchorEl] = React.useState<HTMLElement | null>(
    null
  );
  const [bufferFeet, setBufferFeet] = React.useState<number>(0);

  // Calculate actual spot dimensions using edge lengths (works for any rotation)
  const spotPixelSize = React.useMemo(() => {
    if (!shape.points || shape.points.length < 2) return 100; // fallback

    // Calculate actual edge lengths (not axis-aligned bounding box)
    const edge1Length = Math.sqrt(
      Math.pow(shape.points[1].x - shape.points[0].x, 2) +
      Math.pow(shape.points[1].y - shape.points[0].y, 2)
    );

    if (shape.points.length < 3) return edge1Length;

    const edge2Length = Math.sqrt(
      Math.pow(shape.points[2].x - shape.points[1].x, 2) +
      Math.pow(shape.points[2].y - shape.points[1].y, 2)
    );

    return Math.max(edge1Length, edge2Length);
  }, [shape.points]);

  // Convert pixel size to feet using ramp scale
  const pixelsToFeetRatio = canvasWidth / (ramp?.width ?? 1);
  const spotFeet = Math.round(spotPixelSize / pixelsToFeetRatio);
  const pixelsPerFoot = pixelsToFeetRatio;

  const spotCategory = getDefaultSizeCategory(spotFeet);
  const maxAllowed = getMaxAllowedSizes(spotCategory);

  // Current values
  const currentAllowed = (shape.metadata as SpotMetadata)?.allowedSizes || [
    spotCategory,
  ];
  const currentLaneIds = (shape.metadata as SpotMetadata)?.egressLanes || [];

  // Available egress lanes (sorted alphabetically by text)
  const egressLanes = shapes
    .filter((s) => s.tags?.includes("egress_lane"))
    .sort((a, b) => {
      const aText = a.text || `Lane ${a.id.slice(0, 8)}`;
      const bText = b.text || `Lane ${b.id.slice(0, 8)}`;
      return aText.localeCompare(bText);
    });

  // Helper to update metadata
  const updateMetadata = (updates: Partial<SpotMetadata>) => {
    const updatedShapes = shapes.map((s) => {
      if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
        return {
          ...s,
          metadata: { ...s.metadata, ...updates },
        };
      }
      return s;
    });
    setShapes(updatedShapes);
  };

  // Handle tile grid hover - update preview
  const handleTileHover = (rows: number, cols: number) => {
    if (!ramp?.id || !ramp?.fbo_id) return;
    const bufferPixels = bufferFeet * pixelsPerFoot;
    const tiledSpots = tileSpot(shape, rows, cols, bufferPixels, ramp.id, ramp.fbo_id);
    const updatedShapes = [...shapes, ...tiledSpots];
    const namedShapes = calculateSpotNames(updatedShapes);
    // Extract only the newly added spots (last N items) with their calculated names
    const namedTiledSpots = namedShapes.slice(-tiledSpots.length);
    setPreviewSpots(namedTiledSpots);
  };

  // Handle tile grid selection - add spots to canvas
  const handleTileSelect = (rows: number, cols: number) => {
    if (!ramp?.id || !ramp?.fbo_id) return;
    const bufferPixels = bufferFeet * pixelsPerFoot;
    const tiledSpots = tileSpot(shape, rows, cols, bufferPixels, ramp.id, ramp.fbo_id);
    const updatedShapes = [...shapes, ...tiledSpots];
    const namedShapes = calculateSpotNames(updatedShapes);
    setShapes(namedShapes);
    setPreviewSpots([]);
  };

  // Handle tile picker close - clear preview
  const handleTileClose = () => {
    setTileAnchorEl(null);
    setPreviewSpots([]);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="column" spacing={2}>
        {/* Disabled Toggle */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={(shape.metadata as SpotMetadata)?.disabled || false}
                onChange={(e) => updateMetadata({ disabled: e.target.checked })}
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2">
                  Disabled (exclude from algorithm)
                </Typography>
                <Tooltip title="When enabled, this spot will be unavailable to the parking algorithm. Use for maintenance or construction.">
                  <IconButton size="small" sx={{ p: 0 }}>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 14, color: "text.secondary" }}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          />
        </Box>

        {/* Allowed Sizes */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mb: 1 }}
          >
            <Typography variant="body2">Allowed Aircraft Sizes</Typography>
            <Tooltip title="Spots can only allow aircraft sizes ≤ their own size.">
              <IconButton size="small" sx={{ p: 0 }}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, fontWeight: 600 }}
          >
            This spot: {spotFeet}ft = {spotCategory.toUpperCase()}
          </Typography>

          <FormGroup>
            {SIZE_CATEGORY_OPTIONS.map((size) => {
              const isAllowed = maxAllowed.includes(size.value);
              const isChecked = currentAllowed.includes(size.value);

              return (
                <FormControlLabel
                  key={size.value}
                  disabled={!isAllowed}
                  control={
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...currentAllowed, size.value]
                          : currentAllowed.filter((s) => s !== size.value);
                        updateMetadata({ allowedSizes: updated });
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      color={isAllowed ? "text.primary" : "text.disabled"}
                    >
                      {size.label} {!isAllowed && "(too large)"}
                    </Typography>
                  }
                />
              );
            })}
          </FormGroup>
        </Box>

        {/* Egress Lanes */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mb: 0.5 }}
          >
            <Typography variant="body2">Egress Lanes</Typography>
            <Tooltip title="Select egress lanes this spot can use. Create lanes by drawing Lines and tagging them 'egress_lane'.">
              <IconButton size="small" sx={{ p: 0 }}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={currentLaneIds}
              onChange={(e) =>
                updateMetadata({ egressLanes: e.target.value as string[] })
              }
              renderValue={(selected) => {
                const names = (selected as string[])
                  .map((id) => egressLanes.find((l) => l.id === id)?.text || id)
                  .join(", ");
                return names || "None selected";
              }}
            >
              {egressLanes.length === 0 && (
                <MenuItem disabled>
                  <em>
                    No egress lanes defined (draw Lines tagged "egress_lane")
                  </em>
                </MenuItem>
              )}
              {egressLanes.map((lane) => (
                <MenuItem key={lane.id} value={lane.id}>
                  <Checkbox checked={currentLaneIds.includes(lane.id)} />
                  <ListItemText
                    primary={lane.text || `Lane ${lane.id.slice(0, 8)}`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Gap Spot */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mb: 0.5 }}
          >
            <Typography variant="body2">Gap Spot</Typography>
            <Tooltip title="Mark this spot as a gap spot - smaller spot that overlaps larger spots spatially. Becomes available when surrounding larger spots are occupied.">
              <IconButton size="small" sx={{ p: 0 }}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={(shape.metadata as SpotMetadata)?.isGapSpot || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  const updates: Partial<SpotMetadata> = { isGapSpot: isChecked };

                  // Auto-detect required spots when checked
                  if (isChecked) {
                    const requiredSpotIds = detectRequiredSpots(shape, shapes);
                    updates.requiresOccupiedSpots = requiredSpotIds;
                  } else {
                    // Clear when unchecked
                    updates.requiresOccupiedSpots = [];
                  }

                  updateMetadata(updates);
                }}
              />
            }
            label={
              <Typography variant="body2">
                This is a gap spot
              </Typography>
            }
          />

          {/* Required Spots (auto-detected) */}
          {(shape.metadata as SpotMetadata)?.isGapSpot && (
            <Box sx={{ mt: 1, ml: 4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Required Occupied Spots (auto-detected):
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {((shape.metadata as SpotMetadata)?.requiresOccupiedSpots || []).length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    None detected
                  </Typography>
                ) : (
                  ((shape.metadata as SpotMetadata)?.requiresOccupiedSpots || []).map((id) => {
                    const spot = shapes.find((s) => s.id === id && s.type === "Spot");
                    return (
                      <Chip
                        key={id}
                        label={spot?.text || id.slice(0, 8)}
                        size="small"
                        disabled
                      />
                    );
                  })
                )}
              </Stack>
            </Box>
          )}
        </Box>

        {/* Max Dwell Duration */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mb: 0.5 }}
          >
            <Typography variant="body2">Max Dwell Time</Typography>
            <Tooltip title="Maximum time (in minutes) aircraft can stay in this spot. Leave empty for no limit. Use for overflow/emergency spots.">
              <IconButton size="small" sx={{ p: 0 }}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Minutes"
            placeholder="No limit"
            inputProps={{ min: 0, step: 15 }}
            value={(shape.metadata as SpotMetadata)?.max_dwell_minutes ?? ""}
            onChange={(e) => {
              const value =
                e.target.value === "" ? undefined : Number(e.target.value);
              updateMetadata({ max_dwell_minutes: value });
            }}
            helperText="Examples: 60 (1 hour), 180 (3 hours), 1440 (1 day)"
          />
        </Box>

        {/* Min Dwell Duration */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mb: 0.5 }}
          >
            <Typography variant="body2">Min Dwell Time</Typography>
            <Tooltip title="Minimum time (in minutes) aircraft should stay in this spot. Aircraft staying longer get priority placement here. Use for remote/overflow spots to sequester long-stay aircraft.">
              <IconButton size="small" sx={{ p: 0 }}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Minutes"
            placeholder="No minimum"
            inputProps={{ min: 0, step: 15 }}
            value={(shape.metadata as SpotMetadata)?.min_dwell_minutes ?? ""}
            onChange={(e) => {
              const value =
                e.target.value === "" ? undefined : Number(e.target.value);
              updateMetadata({ min_dwell_minutes: value });
            }}
            helperText="Examples: 480 (8 hours), 1440 (1 day), 4320 (3 days)"
          />
        </Box>

        {/* Blocking Spots - Multi-lane egress support */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            <Typography variant="body2">Blocking Spots (by lane)</Typography>
            <Tooltip title="Spots blocked during egress, calculated per lane. Aircraft uses lane with fewest blocks. Run 'Calculate Blocked Spots' to update.">
              <IconButton size="small" sx={{ p: 0 }}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              </IconButton>
            </Tooltip>
          </Stack>

          {((shape.metadata as SpotMetadata)?.egressOptions || []).length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              None (run "Calculate Blocked Spots")
            </Typography>
          ) : (
            // Multi-lane display
            <Stack spacing={1}>
              {((shape.metadata as SpotMetadata)?.egressOptions || []).map((option) => (
                <Box key={option.laneId}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                    {option.laneName}:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {option.blocksSpotIds.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No blocks
                      </Typography>
                    ) : (
                      option.blocksSpotIds.map((id) => {
                        const spot = shapes.find((s) => s.id === id && s.type === "Spot");
                        return (
                          <Chip
                            key={id}
                            label={spot?.text || id.slice(0, 8)}
                            size="small"
                            disabled
                          />
                        );
                      })
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* Tile Spot */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mb: 1 }}
          >
            <Typography variant="body2">Tiling</Typography>
            <Tooltip title="Create a grid of duplicate spots based on this spot's size and properties">
              <IconButton size="small" sx={{ p: 0 }}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<GridOnIcon />}
              onClick={(e) => setTileAnchorEl(e.currentTarget)}
              size="small"
            >
              Tile Spot
            </Button>
            <MetricAwareTextField
              unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
              size="small"
              label="Buffer"
              value={bufferFeet}
              onChange={(valueInFeet) => setBufferFeet(Math.max(0, valueInFeet || 0))}
              sx={{ width: 85 }}
            />
          </Stack>
          <SpotTilePicker
            anchorEl={tileAnchorEl}
            open={Boolean(tileAnchorEl)}
            onClose={handleTileClose}
            onSelect={handleTileSelect}
            onHover={handleTileHover}
          />
        </Box>
      </Stack>
    </Box>
  );
};
